import TipsAndUpdatesRoundedIcon from "@mui/icons-material/TipsAndUpdatesRounded";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import React from "react";
import { usePagesTranslation } from "../../../../pages/locales";
import {
    getSuggestedHintPointsPackPrice,
    HINT_POINT_COST,
    HINT_POINTS_PACK_AMOUNT,
} from "../lib/hintPointsConstants";
import { useSlidingAppearance } from "../lib/AppearanceContext";
import { settingsPrimaryButtonSx } from "../styles/premiumCtaStyles";
import { systemFont } from "./IOSGroupedList";

/**
 * Settings — buy the assistant coins pack (separate from puzzle-unlock credits).
 */
export default function HintPointsPurchaseSection({
    pointBalance = 0,
    balanceLoading = false,
    packAmount = HINT_POINTS_PACK_AMOUNT,
    packPrice = null,
    isProcessing = false,
    /** Disable while another IAP flow runs (no spinner on this button). */
    purchaseLocked = false,
    isIapAvailable = true,
    onPurchase = null,
}) {
    const { t } = usePagesTranslation();
    const { tokens } = useSlidingAppearance();
    const livePrice = typeof packPrice === "string" && packPrice.trim() ? packPrice.trim() : "";
    const displayPrice = livePrice || getSuggestedHintPointsPackPrice();
    const buttonLabel = t("native.hintPoints.settingsBuyPack", {
        defaultValue: "Get {{amount}} Assistant coins — {{price}}",
        amount: packAmount,
        price: displayPrice,
    });
    const purchaseDisabled = isProcessing || purchaseLocked || !isIapAvailable || !onPurchase;

    return (
        <Box
            sx={{
                mt: 2,
                pt: 2,
                borderTop: `1px solid ${tokens.divider}`,
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
                <TipsAndUpdatesRoundedIcon sx={{ color: "#3BA666", fontSize: 22 }} />
                <Typography
                    sx={{
                        fontFamily: systemFont,
                        fontSize: "0.9375rem",
                        fontWeight: 700,
                        color: tokens.text,
                    }}
                >
                    {t("native.hintPoints.settingsTitle", { defaultValue: "Assistant coins" })}
                </Typography>
            </Box>

            {balanceLoading ? (
                <CircularProgress size={14} sx={{ color: "#3BA666", mb: 1 }} />
            ) : (
                <Typography
                    sx={{
                        fontFamily: systemFont,
                        fontSize: "0.9375rem",
                        fontWeight: 600,
                        color: "#3BA666",
                        mb: 1,
                    }}
                >
                    {t("native.hintPoints.balanceNow", {
                        defaultValue: "You have {{count}} coins now",
                        count: pointBalance ?? 0,
                    })}
                </Typography>
            )}

            <Typography
                sx={{
                    fontFamily: systemFont,
                    fontSize: "0.8125rem",
                    lineHeight: 1.45,
                    color: tokens.textMuted,
                    mb: 1.25,
                }}
            >
                {t("native.hintPoints.settingsDesc", {
                    defaultValue: "Ghost guide assists cost {{cost}} coins each.",
                    cost: HINT_POINT_COST,
                })}
            </Typography>

            <Button
                type="button"
                fullWidth
                variant="contained"
                disabled={purchaseDisabled}
                onClick={() => {
                    void onPurchase?.();
                }}
                sx={settingsPrimaryButtonSx({
                    animate: !purchaseDisabled && !isProcessing,
                })}
            >
                {isProcessing ? (
                    <CircularProgress size={22} sx={{ color: "#ffffff" }} />
                ) : (
                    <Box component="span">{buttonLabel}</Box>
                )}
            </Button>
        </Box>
    );
}
